'use client';
import React, { useState, useEffect } from 'react';
import { Search, Bell, RefreshCw, Calendar, Clock } from 'lucide-react';

interface DashboardTopbarProps {
  onRefresh?: () => void;
  lastRefreshed?: Date | null;
}

export default function DashboardTopbar({ onRefresh, lastRefreshed }: DashboardTopbarProps) {
  const [searchValue, setSearchValue] = useState('');
  const [timeAgo, setTimeAgo] = useState('just now');
  const [spinning, setSpinning] = useState(false);
  const [currentTime, setCurrentTime] = useState('');
  const [today, setToday] = useState('');

  useEffect(() => {
    const updateTime = () => {
      setCurrentTime(new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
      setToday(new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }));
    };
    updateTime();
    const t = setInterval(updateTime, 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!lastRefreshed) return;
    const update = () => {
      const secs = Math.floor((Date.now() - lastRefreshed.getTime()) / 1000);
      if (secs < 10) setTimeAgo('just now');
      else if (secs < 60) setTimeAgo(`${secs}s ago`);
      else setTimeAgo(`${Math.floor(secs / 60)}m ago`);
    };
    update();
    const t = setInterval(update, 5000);
    return () => clearInterval(t);
  }, [lastRefreshed]);

  const handleRefresh = () => {
    setSpinning(true);
    onRefresh?.();
    setTimeout(() => setSpinning(false), 1000);
  };

  return (
    <header className="h-16 bg-card border-b border-border flex items-center px-6 gap-4 flex-shrink-0 z-10">
      {/* Search */}
      <div className="flex-1 max-w-md relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={searchValue}
          onChange={(e) => setSearchValue(e?.target?.value)}
          placeholder="Search events, regions, sectors… (⌘K)"
          className="w-full bg-input border border-border rounded-md pl-9 pr-4 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring transition-colors"
        />
      </div>
      <div className="flex items-center gap-3 ml-auto">
        {/* Data freshness indicator */}
        <div className="hidden lg:flex items-center gap-1.5 text-xs text-muted-foreground border border-border rounded-md px-2.5 py-1.5">
          <Clock size={11} className="text-primary" />
          <span className="font-tabular">{currentTime} UTC</span>
        </div>

        {/* Last updated */}
        <button
          onClick={handleRefresh}
          className="hidden lg:flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          title="Refresh data"
        >
          <RefreshCw size={11} className={`text-primary ${spinning ? 'animate-spin' : ''}`} />
          <span>Updated {timeAgo}</span>
        </button>

        {/* Date */}
        <div className="hidden xl:flex items-center gap-1.5 text-xs text-muted-foreground border border-border rounded-md px-2.5 py-1.5">
          <Calendar size={11} />
          <span>{today}</span>
        </div>

        {/* Notifications */}
        <button className="relative p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
          <Bell size={16} />
          <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-danger rounded-full" />
        </button>

        {/* Live indicator */}
        <div className="flex items-center gap-1.5 text-xs font-medium">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
          </span>
          <span className="text-primary">LIVE</span>
        </div>
      </div>
    </header>
  );
}